import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Video, Plus, Star, Film } from "lucide-react";
import axios from "axios";
import { extractYouTubeId, ytThumbnail } from "@/lib/youtube";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "tour", label: "Property Tour" },
  { key: "drone", label: "Drone / Aerial" },
  { key: "progress", label: "Build Progress" },
  { key: "tides", label: "Tide Timelapse" },
  { key: "wildlife", label: "Wildlife" },
  { key: "general", label: "General" },
];

const DEFAULT_FORM = {
  url: "", title: "", description: "", category: "tour",
  custom_thumbnail_url: "", featured: false,
};

export default function VideoManager({ authHeader }) {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const previewId = extractYouTubeId(form.url);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/videos`, authHeader);
      setVideos(res.data.videos);
    } catch (e) { console.error(e); }
  }, [authHeader]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const resetForm = () => { setForm(DEFAULT_FORM); setEditingId(null); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!editingId && !previewId) {
      setError("Paste a YouTube URL (e.g. https://youtu.be/... or https://youtube.com/watch?v=...)");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/videos/${editingId}`, form, authHeader);
      } else {
        await axios.post(`${API}/admin/videos`, form, authHeader);
      }
      resetForm();
      fetchVideos();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save video.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (v) => {
    setForm({
      url: `https://youtu.be/${v.youtube_id}`,
      title: v.title || "", description: v.description || "",
      category: v.category || "tour",
      custom_thumbnail_url: v.custom_thumbnail_url || "",
      featured: !!v.featured,
    });
    setEditingId(v.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this video? (Safe to do — only removed from the site, YouTube copy stays.)")) return;
    await axios.delete(`${API}/admin/videos/${id}`, authHeader);
    fetchVideos();
  };

  return (
    <div data-testid="video-manager" className="space-y-6">
      {/* Add / Edit form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4 flex items-center gap-2">
          <Film className="w-5 h-5" /> {editingId ? "Edit Video" : "Add Video from YouTube"}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="font-body text-sm text-gray-600">YouTube URL</Label>
            <Input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://youtu.be/dQw4w9WgXcQ  or  https://www.youtube.com/watch?v=..."
              className="mt-1"
              data-testid="video-url-input"
              required={!editingId}
            />
            <p className="font-body text-gray-400 text-xs mt-1">
              Paste any YouTube link — watch URL, youtu.be short link, or Shorts. We auto-extract the video ID.
            </p>
          </div>

          {/* Preview */}
          {previewId && (
            <div className="flex gap-3 items-start p-3 bg-gray-50 rounded">
              <img src={ytThumbnail(previewId, "hqdefault")} alt="" className="w-32 rounded" />
              <div className="flex-1 text-sm">
                <p className="font-body text-gray-500 text-xs mb-1">Preview</p>
                <p className="font-body text-wc-forest font-medium">Video ID: <code className="text-xs bg-gray-200 px-1 rounded">{previewId}</code></p>
                <a href={`https://youtu.be/${previewId}`} target="_blank" rel="noopener noreferrer"
                  className="font-body text-wc-teal text-xs hover:underline">Open on YouTube →</a>
              </div>
            </div>
          )}

          <div>
            <Label className="font-body text-sm text-gray-600">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Drone flyover of the property, April 2026" className="mt-1"
              data-testid="video-title-input" />
          </div>

          <div>
            <Label className="font-body text-sm text-gray-600">Description (optional)</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Short caption shown in the videos grid" className="mt-1" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body text-sm text-gray-600">Category</Label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 font-body text-sm"
                data-testid="video-category-select"
              >
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 cursor-pointer pb-2">
                <input type="checkbox" checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="accent-wc-teal w-4 h-4"
                  data-testid="video-featured-toggle" />
                <Star className="w-4 h-4 text-wc-teal" />
                <span className="font-body text-sm text-gray-700">Featured (appears first on /videos)</span>
              </label>
            </div>
          </div>

          <div>
            <Label className="font-body text-sm text-gray-600">Custom Thumbnail URL (optional)</Label>
            <Input value={form.custom_thumbnail_url}
              onChange={(e) => setForm({ ...form, custom_thumbnail_url: e.target.value })}
              placeholder="Leave blank to use YouTube's auto-thumbnail" className="mt-1" />
          </div>

          {error && <p className="font-body text-red-600 text-sm">{error}</p>}

          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="bg-wc-forest text-white hover:bg-wc-teal font-body gap-2" data-testid="video-save-button">
              <Plus className="w-4 h-4" /> {saving ? "Saving..." : editingId ? "Update Video" : "Add Video"}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm} className="font-body">
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Existing videos */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4 flex items-center gap-2">
          <Video className="w-5 h-5" /> Videos ({videos.length})
        </h3>

        {videos.length === 0 ? (
          <p className="font-body text-gray-400 text-sm py-6 text-center">No videos yet. Add your first one above.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {videos.map(v => (
              <div key={v.id} className="border border-gray-100 rounded-md overflow-hidden bg-white">
                <div className="aspect-video bg-gray-100 relative">
                  <img src={ytThumbnail(v.youtube_id, "hqdefault")}
                    alt={v.title} className="w-full h-full object-cover" />
                  {v.featured && (
                    <span className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-wc-teal text-white text-[10px] rounded font-body uppercase tracking-wider">
                      <Star className="w-3 h-3" /> Featured
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <p className="font-display text-wc-forest font-semibold text-sm mb-1 line-clamp-1">{v.title}</p>
                  <p className="font-body text-gray-500 text-xs mb-2 line-clamp-2">{v.description || <em className="text-gray-300">No description</em>}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                      {CATEGORIES.find(c => c.key === v.category)?.label || v.category}
                    </span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(v)}
                        className="text-wc-teal hover:bg-wc-teal/5 h-7 text-xs font-body">Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(v.id)}
                        data-testid={`video-delete-${v.id}`}
                        className="text-red-500 hover:bg-red-50 h-7 w-7 p-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick instructions */}
      <div className="bg-wc-teal/5 border border-wc-teal/20 rounded-lg p-5">
        <h4 className="font-display text-wc-forest text-sm font-semibold mb-2">Quick guide — uploading from your phone</h4>
        <ol className="font-body text-wc-slate text-sm space-y-1.5 list-decimal list-inside">
          <li>Open Photos on your phone → pick the video → tap Share → <strong>YouTube</strong></li>
          <li>Set visibility (Public for public site, Unlisted for private family links)</li>
          <li>Once uploaded, tap the "Share" icon in YouTube → <strong>Copy link</strong></li>
          <li>Come back here, paste the link, add a title, and tap Add Video</li>
          <li>It's live on the Photography page + /videos page immediately</li>
        </ol>
      </div>
    </div>
  );
}
